import React, {
	PropsWithChildren,
	RefObject,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";

import type { IPreviewOverlayRef } from "../components/preview-overlay";
import type { ISlidesShowPreviewRef } from "../components/slideshow-preview/slideshow-preview";
import type { ISpreadSlideShowItem } from "../components/slideshow-preview/spread-slideshow-item";
import type { ISlideShow } from "../components/slideshow/slideshow-js";
import { useEditor } from "../hooks/useEditor";
import { useGetPreviewBoxStore } from "../hooks/usePreviewBox";
import { useSelectedSpread } from "../hooks/useSelection";
import { Transform, transformPoint } from "../transform";

interface IZoomToAdorner {
	adornerRect: eXpress.math.Rect;
}

interface IZoomToSpread {
	spreadRect: eXpress.math.Rect;
}

const isZoomToSpread = (
	zoomTo: IZoomToSpread | IZoomToAdorner | undefined,
): zoomTo is IZoomToSpread => !!(zoomTo && (zoomTo as IZoomToSpread).spreadRect);

/** Get the rectangle in preview coordinates */
export function calcViewBox(
	slideShow: ISlideShow<ISpreadSlideShowItem> | undefined,
	slideShowItems: Map<eXpress.core.Spread, ISpreadSlideShowItem>,
	spread: eXpress.core.Spread | undefined,
	zoomTo: IZoomToSpread | IZoomToAdorner | undefined,
) {
	if (spread && slideShow) {
		// Get SlideShow item of spread
		const slideShowItem = slideShowItems.get(spread);
		if (slideShowItem) {
			// Get SlideShow viewBox of rect in DIP
			const toPreviewTransform = slideShow.getSlideShowToItemTransform(slideShowItem);

			if (toPreviewTransform) {
				const { sceneGraph } = slideShowItem;
				if (!sceneGraph) return undefined;

				// Zoom entire preview
				if (!zoomTo) {
					const adorner = spread?.getAdorner();
					return adorner
						? sceneGraph.adorner
								.getTransformTo(sceneGraph.preview)
								.transformRect(eXpress.math.rect(adorner.image))
						: sceneGraph.spread
								.getTransformTo(sceneGraph.preview)
								.transformRect(eXpress.math.rect(spread));
				}

				// Zoom region of spread
				if (isZoomToSpread(zoomTo)) {
					return sceneGraph.spread
						.getTransformTo(sceneGraph.preview)
						.transformRect(zoomTo.spreadRect);
				}

				// Zoom region of adorner
				return sceneGraph.adorner
					.getTransformTo(sceneGraph.preview)
					.transformRect(zoomTo.adornerRect);
			}
		}
	}

	return undefined;
}

export interface IPreviewContext {
	mutable: {
		slideShowItems: Map<eXpress.core.Spread, ISpreadSlideShowItem>;
	};
	goTo: (spreadIndex: number) => number;
	goToNext: () => number;
	goToPrev: () => number;

	/** Contains methods exposed by the SlideShowPreview */
	slideShowPreviewRef: RefObject<ISlidesShowPreviewRef>;

	/** Contains methods exposed by the OverleyPreview */
	previewOverlayRef: RefObject<IPreviewOverlayRef>;

	updateZoom: (spread: eXpress.core.Spread, zoomTo?: IZoomToAdorner | IZoomToSpread) => void;

	/** Reset zoom to full spread or to minimal adorner if minimal adorner is set up */
	resetZoom: (spread: eXpress.core.Spread | undefined) => void;

	/** Create a new viewBox to zoom a specific region (all/spread/adorner) */
	calcViewBoxInfo(
		spread: eXpress.core.Spread | undefined,
		zoomTo: IZoomToSpread | IZoomToAdorner | undefined,
	): {
		viewBox: eXpress.math.Rect | undefined;
		viewBoxMargins: eXpress.math.Margins;
	};

	getCenterOfZone(
		startZone: eXpress.core.IZone | undefined | null,
	): eXpress.math.Point | undefined;

	getZonesAtPoint(
		point: { x: number; y: number },
		filter?: (zone: eXpress.core.IZone) => boolean,
	): eXpress.core.IZone[];

	getScreenToSpreadTransform(
		spread: eXpress.core.Spread | undefined,
		logger: eXpress.ILogger | undefined,
	): Transform | undefined;

	isAutoZoom: boolean;
	setIsAutoZoom: (newIsAutoZoom: boolean) => void;

	/** Returns true if the selected spread is zoomed more than the minimum adorner */
	isZoomed(spread: eXpress.core.Spread | undefined): boolean;
}

const PreviewContext = React.createContext<IPreviewContext | undefined>(undefined);

export const PreviewContextProvider = ({ children }: PropsWithChildren) => {
	const editor = useEditor();
	const { selection } = editor;

	const currentSpread = useSelectedSpread();

	const [isAutoZoom, setIsAutoZoom] = useState(false);
	const previewBoxStore = useGetPreviewBoxStore();

	const slideShowPreviewRef = useRef<ISlidesShowPreviewRef>(null);
	const previewOverlayRef = useRef<IPreviewOverlayRef>(null);

	const mutable = useMemo(
		() => ({
			/**
			 * A Map of Spread / SlideShowItems
			 * Stored in a Map so we don't need to loop through the list to get the item of a Spread
			 */
			slideShowItems: new Map<eXpress.core.Spread, ISpreadSlideShowItem>(),
		}),
		[],
	);

	/** Get the slideshow item information for a spread */
	const getSlideshowItemForSpread = useCallback(
		function getSlideshowItemForSpread(
			spread: eXpress.core.Spread | undefined,
			/** Optional: Log when spread is removed from definition */
			logger: eXpress.ILogger | undefined,
		) {
			if (!spread) return undefined;
			const selectedSpreadItem = mutable.slideShowItems.get(spread);
			if (!selectedSpreadItem) return undefined;
			if (!selectedSpreadItem.spread.hasOwner()) {
				// Example:
				// It is possible this is called with an old spread because a component's useSelectedSpread hasn't updated with the right spread yet.
				// Example after a reset, it could be still the previous selected spread that is now removed from the definition)

				logger?.warn(
					`getSlideshowItemForSpread(): requested for '${spread?.toString()}' but is not part (anymore) of a definition.`,
				);
				return undefined;
			}
			return selectedSpreadItem;
		},
		[mutable.slideShowItems],
	);

	const getZonesAtPoint = useCallback(function getZonesAtPoint(
		point: { x: number; y: number },
		filter?: (zone: eXpress.core.IZone) => boolean,
	) {
		const slideShow = slideShowPreviewRef.current?.slideShow;
		if (!slideShow) return [];

		// Get info from slideshow by point
		const { x, y } = point;
		const pointOnScreen = eXpress.math.point(x, y);
		const selectedInfo = slideShow.getSlideShowItemAtPoint(pointOnScreen);

		// Get the zones clicked
		const slideShowPreviewItem = selectedInfo?.item;
		const zones =
			slideShowPreviewItem && selectedInfo
				? slideShowPreviewItem.getZonesAtPoint(selectedInfo.point)
				: [];

		// Filter zones
		return zones.filter((zone) => zone.canSelect() && (!filter || filter(zone)));
	}, []);

	/** Goto another spread and return the index delta, 0 = unchanged */
	const goTo = useCallback(
		function goTo(spreadIndex: number) {
			const { definition } = editor;
			if (!definition) return 0;
			const nextSpread = definition.spreads[spreadIndex];
			if (!nextSpread) return 0;
			if (!currentSpread) return 0;
			const currentSpreadIndex = currentSpread.index;
			const spreadDelta = spreadIndex - currentSpreadIndex;

			selection.setCurrentSpreadAndBroadCast(nextSpread);

			return spreadDelta;
		},
		[currentSpread, editor, selection],
	);

	const goToPrev = useCallback(
		function goToPrev() {
			if (!currentSpread) return 0;
			const currentSpreadIndex = currentSpread.index;
			return goTo(currentSpreadIndex - 1);
		},
		[currentSpread, goTo],
	);

	const goToNext = useCallback(
		function goToNext() {
			if (!currentSpread) return 0;
			const currentSpreadIndex = currentSpread.index;
			return goTo(currentSpreadIndex + 1);
		},
		[currentSpread, goTo],
	);

	/**
	 * Get the margins to stay from the the full screen.
	 * These margin are calculated based on a real PreviewBox element
	 * An extra margin is added to stay from the sides.
	 * We use a larger margin if a zone is selected, so the user can more easily deselect the zone
	 * (If zoomed to the page and there is only one page, use spread margins)
	 */
	const calcViewBoxMargins = useCallback(
		function calcViewBoxMargins(
			viewBox: eXpress.math.IRect | undefined,
			spread: eXpress.core.Spread | undefined,
			/** Don't add extra margins when zoomed in (example for minimalAdorner) */
			noExtraMargin: boolean,
		) {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return eXpress.math.Margins.zero;

			// Example: if a zone is selected with the same dimensions as the spread, (Cover page, full spread zone is selected), use the spread Margins
			const isFullSpread =
				!viewBox || !spread || eXpress.math.rect(spread).isSimilarTo(viewBox);
			const { previewBox } = previewBoxStore;
			return noExtraMargin || isFullSpread
				? eXpress.math.margins(20, 5)
				: eXpress.math.margins(
						Math.max(previewBox.width * 0.1, 30), // Used margin of 10% with a minimum of 30px
						Math.max(previewBox.height * 0.1, 30),
				  );
		},
		[slideShowPreviewRef, previewBoxStore],
	);

	/**
	 * Create a new viewBox to zoom a specific region (all/spread/adorner)
	 */
	const calcViewBoxInfo = useCallback(
		function calcViewBoxInfo(
			spread: eXpress.core.Spread | undefined,
			zoomTo: IZoomToSpread | IZoomToAdorner | undefined,
		) {
			const viewBox = calcViewBox(
				slideShowPreviewRef.current?.slideShow,
				mutable.slideShowItems,
				spread,
				zoomTo,
			);

			// Get the default margin for this
			const viewBoxMargins = calcViewBoxMargins(viewBox, spread, !isZoomToSpread(zoomTo)); // true: the default zoom, doesn't zoom to zones

			return {
				viewBox,
				viewBoxMargins,
			};
		},
		[calcViewBoxMargins, mutable.slideShowItems],
	);

	/**
	 * Get default viewBox for a spread
	 */
	const calcDefaultViewBox = useCallback(
		function calcDefaultViewBox(spread: eXpress.core.Spread | undefined) {
			const adorner = spread?.getAdorner();

			const viewBox = adorner?.minimalAdorner
				? calcViewBox(
						// minimal adorner preview
						slideShowPreviewRef.current?.slideShow,
						mutable.slideShowItems,
						spread,
						{ adornerRect: adorner.minimalAdorner },
				  )
				: undefined; // Don't return viewBox, I assume if set before adorner is loaded, viewbox will go wrong
			const viewBoxMargins = calcViewBoxMargins(viewBox, spread, true); // true: the default zoom, doesn't zoom to zones

			return {
				viewBox,
				viewBoxMargins,
			};
		},
		[calcViewBoxMargins, mutable],
	);

	/**
	 * Function to zoom to a specific region
	 */
	const updateZoom = useCallback(
		function updateZoom(
			spread: eXpress.core.Spread | undefined,
			zoomTo?: IZoomToSpread | IZoomToAdorner,
		) {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const viewBox = calcViewBox(slideShow, mutable.slideShowItems, spread, zoomTo);
			const viewBoxMargins = calcViewBoxMargins(viewBox, spread, !isZoomToSpread(zoomTo));

			slideShow.updateSlideShow(() => {
				slideShow.viewBoxMargins = viewBoxMargins;
				slideShow.viewBox = viewBox;
			});
		},
		[calcViewBoxMargins, mutable],
	);

	const resetZoom = useCallback(
		function resetZoom(spread: eXpress.core.Spread | undefined) {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return;

			const { viewBox, viewBoxMargins } = calcDefaultViewBox(spread);

			slideShow.updateSlideShow(() => {
				slideShow.viewBoxMargins = viewBoxMargins;
				slideShow.viewBox = viewBox;
			});
		},
		[calcDefaultViewBox],
	);

	/** Returns true if the selected spread is zoomed more than the minimum adorner */
	const isZoomed = useCallback(
		function isZoomed(spread: eXpress.core.Spread | undefined) {
			if (!spread) return false;
			const slideshow = eXpress.utils.assert(
				slideShowPreviewRef.current?.slideShow,
				"isZoomed: Slideshow instance is expected",
			);

			// Get SlideShow item of spread
			const slideShowItem = getSlideshowItemForSpread(spread, undefined);
			if (!slideShowItem) return false;

			// Get default Scale (keep minimal adorner into account)
			const { viewBox: defaultViewBox, viewBoxMargins: defaultViewBoxMargins } =
				calcDefaultViewBox(slideShowItem.spread);
			const defaultScale = slideshow.calcViewBoxScale(slideShowItem, {
				newViewBox: defaultViewBox,
				newViewBoxMargins: defaultViewBoxMargins,
			});

			// Get the current zoom factor
			const slideShowTransforms = slideshow.getTransforms();

			const currentScale = slideShowTransforms.slideshow.getTransformTo(
				slideShowTransforms.screen,
			).scale;

			return !!(
				(defaultScale && currentScale > defaultScale * 1.009) // Allow rounding problems
			);
		},
		[calcDefaultViewBox, getSlideshowItemForSpread],
	);

	const getScreenToSpreadTransform = useCallback(
		function getScreenToSpreadTransform(
			spread: eXpress.core.Spread | undefined,
			logger: eXpress.ILogger | undefined,
		) {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!spread || !slideShow) return undefined;

			const slideShowItem = getSlideshowItemForSpread(spread, logger);
			if (!slideShowItem) return undefined;

			// Transform from Screen to Preview Item
			const toPreviewTransform = slideShow.getSlideShowToItemTransform(slideShowItem);
			if (!toPreviewTransform) return undefined;

			// Transform from the Preview item to the Spread
			const { sceneGraph } = slideShowItem;
			if (!sceneGraph) return undefined;

			const spreadToPreviewTransform = sceneGraph.spread.getTransformTo(sceneGraph.preview);

			return spreadToPreviewTransform.mul(toPreviewTransform);
		},
		[getSlideshowItemForSpread],
	);

	const getCenterOfZone = useCallback(
		(startZone: eXpress.core.IZone | undefined | null) => {
			if (!startZone) return undefined;
			const centerDip = startZone.spreadInnerBounds.center();
			const transform = getScreenToSpreadTransform(startZone.spread, editor.logger);
			if (!transform) return undefined;
			return transformPoint(centerDip, transform);
		},
		[getScreenToSpreadTransform, editor.logger],
	);

	const previewContext: IPreviewContext = {
		mutable,
		goTo,
		goToNext,
		goToPrev,
		resetZoom,
		slideShowPreviewRef,
		previewOverlayRef,
		calcViewBoxInfo,
		updateZoom,
		getZonesAtPoint,
		getCenterOfZone,
		isAutoZoom,
		setIsAutoZoom,
		isZoomed,
		getScreenToSpreadTransform,
	};

	// Make previewContext available on console in 'Debug' mode
	// Assuming only one context is used, else global variable overridden
	if (eXpress.isDebugMode) {
		(window as any).previewContext = previewContext;
	}

	return <PreviewContext.Provider value={previewContext}>{children}</PreviewContext.Provider>;
};
PreviewContextProvider.displayName = "PreviewContextProvider";

/**
 * Returns an object with Preview related functions
 * Destructure the result because the returned object is not stable
 */
export const usePreviewContext = () => {
	const previewContext = useContext(PreviewContext);

	// Check if initialized
	if (!previewContext)
		throw new Error("usePreviewContext requires a parent PreviewContextProvider.");

	return previewContext;
};
